import React from 'react';

import {
  TextInput,
  Switch,
  Select,
  Button,
  Stack,
  Group,
  Alert
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';

import { SYSTEM_CONFIGS } from '../../utils/integration';
import { PostJSON } from '../../utils/request';

interface Integration {
  id: number;
  system: string;
  metadata: Record<string, unknown>;
  tz: string;
}

interface IntegrationFormProps {
  integration?: Integration;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface IntegrationResponse {
  success: boolean;
  error?: string;
}

interface IntegrationFormData {
  system: string;
  metadata: Record<string, string | boolean>;
  tz: string;
}

const EXTERNAL_SYSTEMS = Object.values(SYSTEM_CONFIGS).map(config => ({
  value: config.key,
  label: config.name
}));

const TIMEZONES = [
  'US/Eastern',
  'US/Central',
  'US/Mountain',
  'US/Pacific',
  'US/Arizona',
  'US/Alaska',
  'US/Hawaii'
];

const IntegrationForm: React.FC<IntegrationFormProps> = ({ integration, onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const isEditing = !!integration;

  const form = useForm<IntegrationFormData>({
    initialValues: {
      system: integration?.system || '',
      metadata: (integration?.metadata as Record<string, string | boolean>) || {},
      tz: integration?.tz || ''
    },
    validate: {
      system: (value) => (!value ? 'System is required' : null),
      tz: (value) => (!value ? 'Timezone is required' : null),
      metadata: (value, values) => {
        const currentSystemConfig = values.system ? SYSTEM_CONFIGS[values.system] : null;
        if (!currentSystemConfig) {
          return null;
        }
        
        for (const field of currentSystemConfig.metadata) {
          if (field.type === 'boolean') continue;
          const rawValue = value[field.key];
          const fieldValue = typeof rawValue === 'string' ? rawValue.trim() : '';
          if (!fieldValue) {
            return `${field.label} is required for ${currentSystemConfig.name}`;
          }
        }
        return null;
      }
    }
  });

  const currentSystemConfig = form.values.system ? SYSTEM_CONFIGS[form.values.system] : null;

  const handleSubmit = async (values: IntegrationFormData) => {
    try {
      if (!currentSystemConfig) {
        throw new Error('Invalid system selected');
      }

      // Build metadata based on system config
      const metadata: Record<string, unknown> = {};
      
      for (const field of currentSystemConfig.metadata) {
        if (field.type === 'boolean') {
          metadata[field.key] = !!values.metadata[field.key];
          continue;
        }
        const rawVal = values.metadata[field.key];
        const value = typeof rawVal === 'string' ? rawVal.trim() : '';
        if (!value) {
          throw new Error(`${field.label} is required for ${currentSystemConfig.name}`);
        }
        metadata[field.key] = value;
      }

      const url = isEditing ? '/api/integration/edit' : '/api/integration/add';
      const payload = isEditing
        ? { id: integration?.id, metadata, tz: values.tz }
        : { system: values.system, metadata, tz: values.tz };

      const response = await PostJSON<IntegrationResponse>(url, payload);

      if (!response.success) {
        const errorMessage = response.error || `Failed to ${isEditing ? 'update' : 'add'} integration`;
        notifications.show({
          title: 'Error',
          message: errorMessage,
          color: 'red'
        });
        return;
      }

      notifications.show({
        title: 'Success',
        message: `Integration ${isEditing ? 'updated' : 'added'} successfully.`,
        color: 'green'
      });

      if (!isEditing) {
        form.reset();
      }

      if (onSuccess) {
        onSuccess();
      } else {
        void navigate('/account/integrations');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add integration';
      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red'
      });
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <Select
          required
          data={EXTERNAL_SYSTEMS}
          disabled={isEditing}
          label="Integration System"
          placeholder="Select a system"
          {...form.getInputProps('system')}
          onChange={(value) => {
            form.setFieldValue('system', value || '');
            // Clear metadata values when system changes
            form.setFieldValue('metadata', {});
            // Clear metadata validation errors
            form.clearFieldError('metadata');
          }}
        />

        {currentSystemConfig && currentSystemConfig.metadata.map((field) => {
          const metadataError = form.errors.metadata;
          const hasFieldError = typeof metadataError === 'string' && metadataError.includes(field.label);

          if (field.type === 'boolean') {
            return (
              <Switch
                key={field.key}
                checked={!!form.values.metadata[field.key]}
                description={field.description}
                label={field.label}
                onChange={(e) => {
                  form.setFieldValue('metadata', {
                    ...form.values.metadata,
                    [field.key]: e.currentTarget.checked
                  });
                }}
              />
            );
          }

          return (
            <TextInput
              key={field.key}
              required
              autoComplete="off"
              error={hasFieldError ? metadataError : undefined}
              label={field.label}
              placeholder={`Enter ${field.label}`}
              value={(form.values.metadata[field.key] as string) || ''}
              onChange={(e) => {
                form.setFieldValue('metadata', {
                  ...form.values.metadata,
                  [field.key]: e.target.value
                });
                // Clear metadata validation error when user starts typing
                if (form.errors.metadata) {
                  form.clearFieldError('metadata');
                }
              }}
            />
          );
        })}

        <Select
          required
          searchable
          data={TIMEZONES}
          label="Timezone"
          placeholder="Select timezone"
          {...form.getInputProps('tz')}
        />

        {form.errors.metadata && typeof form.errors.metadata === 'string' && (
          <Alert color="red" title="Validation Error">
            {form.errors.metadata}
          </Alert>
        )}

        <Group justify="flex-end" mt="md">
          <Button
            variant="outline"
            onClick={() => {
              if (onCancel) {
                onCancel();
              } else {
                void navigate('/account/integrations');
              }
            }}
          >
            Cancel
          </Button>
          <Button type="submit">
            {isEditing ? 'Save' : 'Add Integration'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default IntegrationForm;

